import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { marker } from '@common/translate-marker';
import { SignInTypes } from '@common/knownTypes/SignInTypes';
import { UserModel } from '@common/models/User.model';
import { getStorageAllKeys } from '@common/utils/storage.util';
import { TranslateService } from '@ngx-translate/core';
import { LogLevel, OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { PcsOidcConfigLoader } from './oidc-config-loader.service';
import { StateService } from 'app/singleton-services/state.service';
import { fromUnixTime, isAfter } from 'date-fns';
import { environment } from 'environments/environment';
import { ToastService } from '@services/toast.service';
import { BehaviorSubject, firstValueFrom, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { currentUserSubject, UserService } from './user.service';
import { MessageService } from '@progress/kendo-angular-l10n';
import { KendoMessageService } from '@common/services/kendoMessage.service';

export const isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

@Injectable()
export class AuthenticationService {
    private _oidcEnabled = false;

    // angular-auth-oidc-client 14 only hands the access token out asynchronously; the interceptor and the
    // report viewer need it synchronously, so the token from the last checkAuth()/forceRefreshSession() is
    // kept here. Every token change in the code flow goes through one of those two calls.
    private oidcAccessToken: string | null = null;

    private rawHttp: HttpClient; // raw http client does not include interceptors

    constructor(
        private oidcSecurityService: OidcSecurityService,
        private oidcConfigLoader: PcsOidcConfigLoader,
        private http: HttpClient,
        private handler: HttpBackend,
        private userService: UserService,
        private router: Router,
        private stateService: StateService,
        private translateService: TranslateService,
        private toastyService: ToastService,
        private messageService: MessageService,
    ) {
        this.rawHttp = new HttpClient(handler);
    }

    public get oidcEnabled() {
        return this._oidcEnabled;
    }

    /**
     * Checks local authentication data, completes oidc authentication id state data present, check tokens.
     * @returns
     */
    async chekcAuthenticationData(): Promise<boolean> {
        // not authenticated
        let status = true;

        // oidc security check
        if (this._oidcEnabled) {
            const loginResponse = await firstValueFrom(this.oidcSecurityService.checkAuth());
            this.oidcAccessToken = loginResponse.accessToken || null;
            status = loginResponse.isAuthenticated;
        }

        // check jwt status
        const jwtStatus = this.isJwtAuthenticated();

        // result status
        const result = status && jwtStatus;

        // update data to local storage and return;
        this.updateIsAuthenticatedSubject(result);
        return result;
    }

    private isJwtAuthenticated() {
        try {
            const payload = this.getAccessTokenData();

            if (!payload) {
                return false;
            }

            const expireDate = fromUnixTime(payload.exp);
            return isAfter(expireDate, new Date());
        } catch (ex) {
            console.error(ex);
            return false;
        }
    }

    private getAccessTokenData(): any {
        const token = this.getAccessToken();
        if (!token) {
            return null;
        }

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );

        return JSON.parse(jsonPayload);
    }

    attemptCertLogin(candidate: any) {
        return this.http.post(`${environment.certApiUrl}/authentication/Login`, candidate);
    }

    /**
     * Loads current user from API service.
     * @returns
     */
    async loadCurrentUser(): Promise<any> {
        const signInType = this.stateService.getSignInType();
        // A logout can finish while this request is still in flight: when a tab is reopened after a break,
        // StartupService loads the user while the session-timeout logout started by AppComponent is already
        // running. Publishing that late answer put the sidebar and the user menu back on the login page.
        const tokenGeneration = UserService.getTokenGeneration();
        try {
            const data = await firstValueFrom(this.http.get(`api:///authentication/getCurrentUser/${signInType}`));
            if (UserService.getTokenGeneration() !== tokenGeneration) {
                return null;
            }

            let user = null;
            if (data) {
                user = new UserModel(data);

                const isImpersonation = user.signInType && user.signInType === SignInTypes.impersonation;
                if (isImpersonation && !sessionStorage.getItem('impersonationOriginalToken')) {
                    try {
                        const result: { originalSignInType: string; originalLanguage: string } = await firstValueFrom(
                            this.userService.exitImpersonation()
                        );

                        if (result.originalLanguage) {
                            localStorage.setItem('language', result.originalLanguage);
                            this.translateService.use(result.originalLanguage.toLowerCase());

                            const msgs = <KendoMessageService>this.messageService;
                            msgs.language = result.originalLanguage.toLowerCase();
                        }

                        this.toastyService.warning(
                            this.translateService.instant(marker('Exited "Login as another user" session.'))
                        );
                    } catch (e) {
                        console.error('Failed to close "Login as" session on backend', e);
                    }

                    this.clearUser();
                    this.updateIsAuthenticatedSubject(false);
                    this.updateCurrentUser(null);

                    this.redirectToLogin(null);

                    return null;
                }

                this.stateService.setSignInType(user.signInType);
            }
            this.updateIsAuthenticatedSubject(!!data);
            this.updateCurrentUser(user);
            return user;
        } catch (error) {
            this.updateIsAuthenticatedSubject(false);
            this.updateCurrentUser(null);
            return null;
        }
    }

    async authenticateWithTokens(accessToken: string, refreshToken: string) {
        // oidc mode
        if (this._oidcEnabled) {
            throwError('Action not supported in OIDC mode.');
            return;
        }

        // local login
        this.userService.setAuthenticationTokens(accessToken, refreshToken);
        const status = await this.loadCurrentUser();
        if (!status) {
            await this.logout();
        }
        return status;
    }

    getAccessToken() {
        // oidc token
        if (this._oidcEnabled) {
            return this.oidcAccessToken;
        }

        // legacy token
        return UserService.getAccessToken();
    }

    refreshAccessToken() {
        // oidc
        if (this._oidcEnabled) {
            // LoginResponse carries accessToken, which is what the interceptor reads from the result
            return this.oidcSecurityService.forceRefreshSession().pipe(
                tap((response) => {
                    this.oidcAccessToken = response.accessToken || null;
                }),
                map((response) => response as any)
            );
        }

        // legacy
        return this.userService.refreshAccessToken();
    }

    async logout(returnUrl?: string, localLogout?: boolean) {
        // should not fail
        try {
            // execute logout
            if (this._oidcEnabled) {
                if (localLogout) {
                    this.oidcSecurityService.logoffLocal();
                } else {
                    this.oidcSecurityService.logoff();
                }
            } else {
                await this.userService.logout();
            }
        } catch (e) {
            console.log(e);
        }

        // clear tokens
        this.clearLocalTokens();
        this.oidcAccessToken = null;

        // notify logout
        this.updateIsAuthenticatedSubject(false);
        this.updateCurrentUser(null);
    }

    async logoutAndRedirectToLogin(returnUrl?: string, localLogout = false) {
        await this.logout(returnUrl, localLogout);
        this.redirectToLogin(returnUrl);
        this.stateService.clearFilters();
    }

    clearUser() {
        this.clearLocalTokens();
        this.updateCurrentUser(null);
    }

    clearUserAndRedirectToLogin(returnUrl: string) {
        this.clearUser();
        this.redirectToLogin(returnUrl);
    }

    redirectToLogin(returnUrl: string) {
        if (returnUrl) {
            this.router.navigate(['/login'], { queryParams: { returnUrl: returnUrl } });
        } else {
            this.router.navigate(['/login']);
        }
    }

    /** Drops the "Login as another user" bookkeeping kept in sessionStorage. Cleared on every logout:
     *  it used to survive a session timeout, which both kept the impersonation banner alive on the
     *  login page and made loadCurrentUser() below believe an orphaned impersonation was still valid. */
    clearImpersonationState() {
        sessionStorage.removeItem('impersonationOriginalToken');
        sessionStorage.removeItem('impersonationOriginalRefreshToken');
        sessionStorage.removeItem('impersonationTargetUserName');
    }

    clearLocalTokens() {
        // pcs tokens
        this.userService.clearLocalTokens();

        // impersonation tokens
        this.clearImpersonationState();

        // oidc tokens
        if (environment.oidcClient != null) {
            getStorageAllKeys()
                .filter(
                    (x) =>
                        x.startsWith(environment.oidcClient) && x != environment.oidcClient + '_authWellKnownEndPoints'
                )
                .forEach((x) => {
                    localStorage.removeItem(x);
                });
        }
    }

    async configureOidc(): Promise<boolean> {
        try {
            // load basic configuration from server
            const data = (await firstValueFrom(this.rawHttp.get(environment.apiUrl + '/authentication/oidc'))) as any;
            if (data == null) {
                return false;
            }

            // setup oidc client. configId doubles as the localStorage key prefix the library uses,
            // which is what clearLocalTokens() looks for.
            const cfg = {
                configId: environment.oidcClient,
                authority: data.oidcAuthority,
                postLoginRoute: '/login/oidc/callback',
                redirectUrl: window.location.origin + '/login/oidc/callback',
                postLogoutRedirectUri: window.location.origin + '/login',
                ignoreNonceAfterRefresh: true,
                clientId: environment.oidcClient,
                disableRefreshIdTokenAuthTimeValidation: true,
                maxIdTokenIatOffsetAllowedInSeconds: 300,
                scope: environment.oidcScope,
                responseType: 'code',
                silentRenew: false,
                useRefreshToken: true,
                logLevel: LogLevel.Warn
            } as OpenIdConfiguration;
            this.oidcConfigLoader.setConfig(cfg);

            // set oidc enabled status
            this._oidcEnabled = true;

            return true;
        } catch (error) {
            if (error.status == 404) {
                return false;
            }
            throw error;
        }
    }

    public oidcAuthorize() {
        if (this.oidcEnabled) {
            this.oidcSecurityService.authorize();
        }
    }

    private updateIsAuthenticatedSubject(value) {
        if (isAuthenticatedSubject.getValue() !== value) {
            isAuthenticatedSubject.next(value);
        } else {
            isAuthenticatedSubject.next(value);
        }
    }

    private updateCurrentUser(user) {
        if (currentUserSubject.getValue() !== user) {
            currentUserSubject.next(user);
        } else {
            currentUserSubject.next(user);
        }
    }

    downloadRegistrationForm() {
        return this.http.get<any>(`api:///authentication/downloadRegistrationForm`);
    }
}
